import { useCallback, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/styles';
import { MessageList, useChannelStateContext } from 'stream-chat-react';
import { UnknownType, UserResponse } from 'stream-chat';

import { useChatClient } from '@bizzabo/chat-core';

import useTypeTracking from '../../../../../analytics';
import { useMessages, View } from '../../../contexts/MessagesContext';
import { useReadMessageEvent } from './useReadMessageEvent';
import CardMessage from '../CardMessage';
import MessageWrapper from '../MessageWrapper';

type SnowplowInfoPayload = {
    action: string;
    reciever_id?: string;
    sender_id?: string;
};

interface CustomMessageListProps {
    channelStatus: number;
    startMeetingIndex: number;
    guestUser: UserResponse<UnknownType>;
    guestProfileId: number;
}

const CustomMessageList: React.FC<CustomMessageListProps> = ({
    channelStatus,
    startMeetingIndex,
    guestUser,
    guestProfileId,
}) => {
    const { channel, messages } = useChannelStateContext();
    const { view } = useMessages();
    const { client } = useChatClient();
    const classes = useStyles();
    const { trackSystemEvent } = useTypeTracking();
    const { id: channelId } = channel;
    const isCalled = useRef(false);

    const sendInfoEvent = useCallback(
        (payload: SnowplowInfoPayload) => {
            trackSystemEvent({
                description: payload,
            });
        },
        [trackSystemEvent]
    );

    useReadMessageEvent(channel, client, view, channelId, sendInfoEvent, guestProfileId);

    useEffect(() => {
        if (view === View.Channel && guestProfileId && !isCalled.current) {
            isCalled.current = true;

            const payload = {
                action: 'CHANNEL_VIEWED',
                reciever_id: String(guestProfileId),
            };

            sendInfoEvent(payload);
        }
    }, [view, guestProfileId, sendInfoEvent]);

    return (
        <div className={classes.messageListWrapper}>
            {!messages.length ? (
                <CardMessage
                    emoji={'👋'}
                    text={'Start by saying Hi! <br /> Introduce yourself or ask a question'}
                />
            ) : (
                <MessageList
                    Message={() => (
                        <MessageWrapper
                            channelStatus={channelStatus}
                            startMeetingIndex={startMeetingIndex}
                            guestUser={guestUser}
                        />
                    )}
                    disableDateSeparator
                    noGroupByUser
                />
            )}
        </div>
    );
};

const useStyles = makeStyles(
    theme => ({
        messageListWrapper: {
            position: 'relative',
            height: '100%',
            backgroundColor: theme.palette.greys[100],

            '& .str-chat': {
                height: '100%',
            },
            '& .str-chat__list': {
                height: '100%',
                '& > div': {
                    padding: 0,
                },
            },
            '& .str-chat__ul': {
                position: 'absolute',
                width: '100%',
                height: '100%',
            },
            '& .str-chat__li': {
                margin: 0,
                '&:last-child': {
                    paddingBottom: 16,
                },
            },
            '& .str-chat__thread-list': {
                paddingTop: 0,
            },
            '& .str-chat__reverse-infinite-scroll': {
                position: 'relative',
            },
        },
    }),
    { classNamePrefix: CustomMessageList.name }
);

export default CustomMessageList;
